import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { fetchPortfolio } from "../lib/api";
import { X, ZoomIn } from "lucide-react";

const CATS = [
    { id: "all", label: "All Work" },
    { id: "small", label: "Small" },
    { id: "custom", label: "Custom" },
    { id: "sleeves", label: "Sleeves" },
    { id: "coverups", label: "Coverups" },
];

const aspectClass = (a) => {
    if (a === "tall") return "row-span-2 aspect-[3/4]";
    if (a === "wide") return "col-span-2 aspect-[16/10]";
    return "aspect-square";
};

export const Portfolio = () => {
    const [active, setActive] = useState("all");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState(null);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        fetchPortfolio(active)
            .then((data) => {
                if (mounted) setItems(data);
            })
            .catch(() => mounted && setItems([]))
            .finally(() => mounted && setLoading(false));
        return () => {
            mounted = false;
        };
    }, [active]);

    const openLightbox = (item) => {
        setCurrent(item);
        setOpen(true);
    };

    return (
        <section
            id="portfolio"
            data-testid="portfolio-section"
            className="relative py-24 md:py-32 bg-black overflow-hidden"
        >
            {/* Big stencil bg word */}
            <div className="absolute -top-10 left-0 right-0 pointer-events-none opacity-[0.05] overflow-hidden">
                <div className="font-display text-[18rem] leading-none whitespace-nowrap text-white">
                    PORTFOLIO · PORTFOLIO ·
                </div>
            </div>

            <div className="relative max-w-7xl mx-auto px-6 md:px-10">
                <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="h-px w-10 bg-[#7CFC00]" />
                            <span className="text-[#7CFC00] uppercase tracking-[0.3em] text-xs font-semibold">
                                The Gallery
                            </span>
                        </div>
                        <h2
                            data-testid="portfolio-title"
                            className="font-display text-5xl md:text-7xl text-white tracking-tight uppercase"
                        >
                            Recent <span className="text-[#7CFC00] text-glow">Work</span>
                        </h2>
                        <p className="mt-4 text-zinc-400 max-w-xl">
                            Every piece is custom. Every line earned. Tap any piece to expand.
                        </p>
                    </div>

                    <div
                        data-testid="portfolio-filters"
                        className="flex flex-wrap gap-2"
                    >
                        {CATS.map((c) => (
                            <button
                                key={c.id}
                                data-testid={`filter-${c.id}`}
                                onClick={() => setActive(c.id)}
                                className={`px-5 py-2.5 text-xs md:text-sm uppercase tracking-widest font-semibold border transition-all ${
                                    active === c.id
                                        ? "bg-[#7CFC00] text-black border-[#7CFC00] glow-neon"
                                        : "bg-transparent text-zinc-300 border-white/15 hover:border-[#7CFC00] hover:text-[#7CFC00]"
                                }`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div data-testid="portfolio-loading" className="text-zinc-500 py-20 text-center uppercase tracking-widest text-sm">
                        Loading work...
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-zinc-500 py-20 text-center uppercase tracking-widest text-sm">
                        No pieces in this category yet.
                    </div>
                ) : (
                    <div
                        data-testid="portfolio-grid"
                        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 auto-rows-[180px] md:auto-rows-[220px]"
                    >
                        {items.map((it, idx) => (
                            <button
                                key={it.id}
                                data-testid={`portfolio-item-${it.id}`}
                                onClick={() => openLightbox(it)}
                                className={`group relative overflow-hidden border border-white/10 hover:border-[#7CFC00] transition-all ${aspectClass(
                                    it.aspect
                                )}`}
                                style={{ animationDelay: `${idx * 60}ms` }}
                            >
                                <img
                                    src={it.image}
                                    alt={it.title}
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />
                                <div className="absolute top-3 left-3 z-10">
                                    <span className="bg-black/70 backdrop-blur-sm border border-white/20 group-hover:border-[#7CFC00] text-[10px] uppercase tracking-widest text-white px-2 py-1">
                                        {it.category}
                                    </span>
                                </div>
                                <div className="absolute bottom-3 left-3 right-3 z-10 flex items-end justify-between">
                                    <div className="text-left">
                                        <div className="font-display text-lg md:text-xl text-white tracking-wide">
                                            {it.title}
                                        </div>
                                    </div>
                                    <div className="bg-[#7CFC00] text-black p-2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all">
                                        <ZoomIn size={16} />
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    data-testid="lightbox-dialog"
                    className="bg-black border border-[#7CFC00]/40 max-w-4xl p-0 rounded-none"
                >
                    {current && (
                        <div className="relative">
                            <VisuallyHidden.Root>
                                <DialogTitle>{current.title}</DialogTitle>
                                <DialogDescription>
                                    {current.description || `${current.category} tattoo by TATZBYB4NK`}
                                </DialogDescription>
                            </VisuallyHidden.Root>
                            <button
                                data-testid="lightbox-close"
                                onClick={() => setOpen(false)}
                                className="absolute top-3 right-3 z-20 bg-black/80 border border-white/20 text-white p-2 hover:border-[#7CFC00] hover:text-[#7CFC00] transition-colors"
                            >
                                <X size={18} />
                            </button>
                            <img
                                src={current.image}
                                alt={current.title}
                                className="w-full max-h-[75vh] object-contain bg-black"
                            />
                            <div className="p-6 border-t border-white/10">
                                <div className="flex items-center gap-3 mb-2">
                                    <span className="bg-[#7CFC00] text-black text-[10px] uppercase tracking-widest font-bold px-2 py-1">
                                        {current.category}
                                    </span>
                                </div>
                                <div className="font-display text-2xl md:text-3xl text-white">
                                    {current.title}
                                </div>
                                {current.description && (
                                    <p className="text-zinc-400 mt-2">{current.description}</p>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </section>
    );
};
